import { Box, Grid, IconButton, Stack } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { DatePicker } from 'components/styleguide';
import { LETTER_TYPES } from 'utils/typed_constants';

type TNullableDate = Date | null;

type TLetterRow = {
  type: typeof LETTER_TYPES;
  /** The label shown ABOVE the field — `Sent`, `Received`, `Expires`, `Partially Executed`.
   *  The section heading already says which letter it belongs to, so it is not repeated. */
  status: string;
  readOnly?: boolean;
  canSendLetters?: boolean;
  onChange: (arg0: TNullableDate) => void;
  value: TNullableDate; // date
  /** Defaults to `<type>-<status>`. A date that names its own field passes it here, so the
   *  input id stays a stable identifier rather than a label with spaces in it. [QW-329] */
  name?: string;
};

/**
 * One date — a third of a row. Three sit side by side inside a `letter-row` container.
 *
 * The label sits above the field and the trashcan beside it, matching the arrangement Qwire
 * asked for. There is no separate label column: the field's own label carries it, which is
 * what lets three fit across where two did before.
 */
export const LetterRow = ({
  type,
  status,
  readOnly = false,
  canSendLetters = true,
  onChange,
  value,
  name,
}: TLetterRow) => {
  const editable = !readOnly && canSendLetters;

  return (
    <Grid item xs={4}>
      <Stack direction="row" alignItems="flex-end" spacing={0.5}>
        {/* The standard date field. `popover` opens the existing picker widget — the one
            carrying the relative-date section Marc keeps — alongside the typed input, so
            typing and picking produce the same result. */}
        <DatePicker
          popover={editable}
          readOnly={!editable}
          name={name ?? `${type}-${status}`}
          label={status}
          value={value}
          onChange={onChange}
        />

        {/* The trashcan sits beside the field it clears, and its presence follows the
            field's CURRENT value in the drawer rather than what is stored: a date typed
            and unsaved shows one, a date cleared and unsaved does not.

            Its slot is always reserved, even when no icon is shown. Otherwise the field
            shrinks the moment a date is entered and grows again when it is cleared, and
            fields in the same column end up different widths depending on whether they
            happen to hold a value.

            The icon carries no `color` prop, which is the house standard - the other fifteen
            trashcans in this app render the default grey. `color="error"` made this one solid
            red and the odd one out, and Qwire's reference shows it grey. */}
        <Box sx={{ width: 30, flexShrink: 0 }}>
          {editable && value && (
            <IconButton
              aria-label={status}
              data-testid="button-clear-date"
              onClick={() => onChange(null)}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Stack>
    </Grid>
  );
};
